import { ProfileCard } from '@/features/unifiedProfileCard'
import { useStyles } from './styles'
import {
  $externalProfile,
  $xtGigi,
  $userLanguage,
  $userToken,
  deleteExternalProfile,
  getFullExternalProfileFx,
} from '@/entities/user'
import { lang } from '@/shared/constants/lang'
import { BackgroundShading } from '@/shared/ui/backgroundShading'
import { useUnit } from 'effector-react'
import { Statistics } from '@/shared/ui/dataWrappers/farcanaStatistics'
import { FormattedStat } from '@/shared/constants/types'
import { GameType } from '@/entities/platforms/steam/types/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { GamesList } from '@/features/profileTabs/gameList'
import { Icon } from '@/shared/ui/icon'
import { $externalUserTid, $fromSocialTab, $xTgamedId } from '@/entities/navigation/store'
import useFormatExternalUnifiedStats from './hooks/useFormatUnifiedStats'
import { useHideNav } from '@/shared/hooks/useHideNav'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { ExternalProfileSkeleton } from './skeleton'
import { goToSocial } from '@/app/routing/events'

export const ExternalUnifiedProfileView = () => {
  useHideNav()

  const { classes } = useStyles()

  const userToken = useUnit($userToken)
  const userLang = useUnit($userLanguage)
  const texts = lang[userLang].pages.profile
  const externalProfile = useUnit($externalProfile)
  const socialTabRedirectOrigin = useUnit($fromSocialTab)
  const xtTid = useUnit($externalUserTid)
  const xtGamedId = useUnit($xTgamedId)
  const gamesList = externalProfile?.games
  const gigi = useUnit($xtGigi)
  useBackButton({
    onBack: () => handleBack(),
  })

  const handleBack = useCallback(() => {
    deleteExternalProfile()
    if (socialTabRedirectOrigin !== undefined) {
      goToSocial({ tab: socialTabRedirectOrigin })
    } else {
      window.history.back()
    }
  }, [socialTabRedirectOrigin])
  const stats: FormattedStat[] = useFormatExternalUnifiedStats()

  const favoriteGames = useMemo<GameType[] | null>(() => {
    if (!gamesList?.length) return null
    const g = [...gamesList].sort((a, b) => b.total_hours - a.total_hours).slice(0, 5)
    return g
  }, [gamesList])

  const [localLoading, setLocalLoading] = useState(false)
  useEffect(() => {
    setLocalLoading(true)
    getFullExternalProfileFx({ token: userToken!, friend_tid: xtTid }).finally(() => {
      setLocalLoading(false)
    })
  }, [userToken, xtTid])

  if (!externalProfile || localLoading) return <ExternalProfileSkeleton />

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <img src="/assets/subscriptionRelated/gamedLogo.webp" className={classes.logoImg} />
        <BackgroundShading bottom="0" height="112px" />
      </div>

      <div className={classes.content}>
        <ProfileCard
          variant={externalProfile.is_private ? 'external_private' : 'external'}
          balance={gigi}
          xtGamedId={xtGamedId}
        />
        <h1 className={classes.title}>{texts.externalGamingLegacy}</h1>
        {externalProfile.is_private ? (
          <PrivateSection />
        ) : (
          <div className={classes.stats}>
            {stats.map((item, index) => (
              <Statistics key={index} value={item.value} name={item.name} />
            ))}
          </div>
        )}
        <h1 className={classes.title}>{texts.favoriteGames}</h1>
        {externalProfile.is_private ? (
          <PrivateSection />
        ) : (
          favoriteGames && (
            <>
              <GamesList gamesList={favoriteGames} />
            </>
          )
        )}
      </div>
    </div>
  )
}

function PrivateSection() {
  const { classes } = useStyles()
  const userLang = useUnit($userLanguage)
  const texts = lang[userLang].pages.profile
  return (
    <div className={classes.privateAccountContainer}>
      <div className={classes.privateAccountWrapper}>
        <Icon view="privateEye" width={24} />
        <span>{texts.privateAccount}</span>
      </div>
    </div>
  )
}
